import { CompanyDialogComponent, CompanyDialogData } from '@/components/company-dialog/company-dialog.component';
import {
  CompanySettingsDialogComponent,
  CompanySettingsDialogData
} from '@/components/company-settings-dialog/company-settings-dialog.component';
import { ConfirmDialogComponent } from '@/components/confirm-dialog/confirm-dialog.component';
import { Paging } from '@/models/Paging';
import { AlertService } from '@/services/alert.service';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CompaniesService, Company } from '@eemo/api-client';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { FullAddressPipe } from '../../../extensions/full-address.pipe';
import { CommaStringArrayPipe } from '../../../extensions/commaStringArrayPipe';
import { DateTimeFormatPipe } from '@/extensions/dateTimePipe';
import { NgIf } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';

import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
    selector: 'app-admin-companies',
    templateUrl: './admin-companies.component.html',
    styleUrls: ['./admin-companies.component.scss'],
    standalone: true,
    imports: [MatFormFieldModule, MatIconModule, MatInputModule, MatSlideToggleModule, MatButtonModule, MatTableModule, MatSortModule, MatMenuModule, MatPaginatorModule, NgIf, DateTimeFormatPipe, CommaStringArrayPipe, FullAddressPipe]
})
export class AdminCompaniesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource: MatTableDataSource<Company>;
  displayFullColumns = false;
  tableClass = 'light';
  lightColumns = ['name', 'services', 'summary', 'city', 'email', 'phoneNumber', 'webSite', 'active', 'actions'];
  fullColumns = [
    'name',
    'services',
    'summary',
    'description',
    'address',
    'email',
    'phoneNumber',
    'webSite',
    'active',
    'vatNumber',
    'siret',
    'dateCreate',
    'uid',
    'actions'
  ];
  displayedColumns = [];
  resultsLength = 0;
  isLoadingResults = true;

  sub = new Subscription();
  deleteSub = new Subscription();
  activateSub = new Subscription();
  deActivateSub = new Subscription();

  paging = new Paging();

  constructor(
    private companiesService: CompaniesService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private translate: TranslateService,
    private changeDetectorRefs: ChangeDetectorRef
  ) {
    this.displayedColumns = this.lightColumns;
  }

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.deleteSub.unsubscribe();
    this.activateSub.unsubscribe();
    this.deActivateSub.unsubscribe();
  }

  loadData() {
    let filter: string;
    if (this.dataSource && this.dataSource.filter) {
      filter = this.dataSource.filter;
    }
    // TODO manage pagination
    this.sub = this.companiesService.getCompanies().subscribe((data) => {
      const displayedData = [];
      data.list.forEach((i) => {
        const row = {
          company: i,
          name: i.name,
          email: i.email,
          city: i.address ? i.address.city : '',
          uid: i.uid,
          services: i.services.map((s) => this.translate.instant('enum.ServiceType.' + s))
        };
        displayedData.push(row);
      });
      this.dataSource = new MatTableDataSource(displayedData);
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      if (filter) {
        this.applyFilter(filter);
      }
    });
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  toggleDisplayColumns() {
    if (this.displayFullColumns) {
      this.displayFullColumns = false;
      this.displayedColumns = this.lightColumns;
      this.tableClass = 'light';
    } else {
      this.displayFullColumns = true;
      this.displayedColumns = this.fullColumns;
      this.tableClass = 'full';
    }
    this.changeDetectorRefs.detectChanges();
  }

  add() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(CompanyDialogComponent, {
        // width: '20rem'
        data: new CompanyDialogData()
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  edit(companyToEdit: Company) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(CompanyDialogComponent, {
        data: { company: companyToEdit, edition: true }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  settings(companyToEdit: Company) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: CompanySettingsDialogData = { company: companyToEdit };
      const dialogRef = this.dialog.open(CompanySettingsDialogComponent, {
        data
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  confirmDelete(company: Company) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la suppression du partenaire "' + company.name + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete(company);
        }
      });
    }
  }

  doDelete(company: Company) {
    this.deleteSub = this.companiesService.deleteCompany(company.uid).subscribe((result) => {
      this.alertService.success('Suppression effectuée avec succès');
      this.loadData();
    });
  }

  confirmActivate(company: Company) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous l\'activation du partenaire "' + company.name + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doActivate(company);
        }
      });
    }
  }

  doActivate(company: Company) {
    this.activateSub = this.companiesService.manageCompanyActivation(company.uid, true).subscribe((result) => {
      this.alertService.success('Partenaire activée');
      this.loadData();
    });
  }

  confirmDeActivate(company: Company) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la désactivation du partenaire "' + company.name + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeActivate(company);
        }
      });
    }
  }

  doDeActivate(company: Company) {
    this.deActivateSub = this.companiesService.manageCompanyActivation(company.uid, false).subscribe((result) => {
      this.alertService.success('Partenaire désactivé');
      this.loadData();
    });
  }
}
